import React, { useMemo, type AnchorHTMLAttributes } from 'react'
import { Flex, TableColumn, TableSquare, Text, TextVariant, InfoIcon, Tooltip } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { CycleConfig, LicenseType, NodesByCycleIdData } from '@mprolab/sdk'
import { MediaQuery } from '@mprolab/uikit/src/theme/base'
import { DURATION_LANGUAGE, displayDuration, formatPrice, getDurationInMilliseconds } from 'views/Rewards/rewards.logic'
import { observer } from 'mobx-react-lite'
import { CheckCircle, MoreVertical } from 'react-feather'

dayjs.extend(isBetween)

const StyledHeaderTitle = styled(Text).attrs(() => ({
  variant: TextVariant.BODY_SMALL
}))`
  color: ${({ theme }) => theme.colors.white3};
  display: flex;
  align-items: center;
  gap: 8px;
`
const StyledLink = styled.a<AnchorHTMLAttributes<HTMLAnchorElement>>`
  color: ${({ theme }) => theme.colors.purple};
  text-decoration: underline;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  :hover {
    color: ${({ theme }) => theme.colors.purpleHover};
  }
`

interface NodesPerCycleLogViewProps {
  logData: NodesByCycleIdData[]
}

const NodesPerCycleLogView: React.FC<NodesPerCycleLogViewProps> = observer(({ logData }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()

  const columns = useMemo(
    (): TableColumn[] => [
      {
        key: 'node',
        name: t('rewards.node'),
        gridColumns: 4,
        customComponent(log: NodesByCycleIdData) {
          return (
            <Text variant={TextVariant.BODY_SMALL} isTrimmed linesToDisplay={1}>
              {log.license?.name || log.license.licenseId}
            </Text>
          )
        }
      },
      {
        key: 'activeTime',
        name: (
          <StyledHeaderTitle>
            {t('rewards.activeTime')}
            <InfoIcon
              iconProps={{ size: 18 }}
              tooltipProps={{
                title: t('rewards.activeTimeInfo'),
                placement: 'top'
              }}
            />
          </StyledHeaderTitle>
        ),
        gridColumns: 3,
        customComponent(log: NodesByCycleIdData) {
          switch (true) {
            case log.credits > 0:
              return <CheckCircle size={16} color={colors.green} />
            default:
              return (
                <Text variant={TextVariant.BODY_SMALL} color={colors.white5}>
                  -
                </Text>
              )
          }
        }
      },
      {
        key: 'credits',
        name: (
          <Flex fullWidth justifyContent="flex-end">
            <StyledHeaderTitle>
              {t('rewards.credits')}
              <InfoIcon
                iconProps={{ size: 18 }}
                tooltipProps={{
                  title: t('rewards.creditsInfo'),
                  placement: 'top'
                }}
              />
            </StyledHeaderTitle>
          </Flex>
        ),
        gridColumns: 2,
        align: 'flex-end',
        customComponent(log: NodesByCycleIdData) {
          return (
            <Flex justifyContent="flex-end">
              <Text variant={TextVariant.BODY_SMALL_BOLD} isTrimmed linesToDisplay={1}>
                {formatPrice(log.credits)}
              </Text>
            </Flex>
          )
        }
      },
      {
        key: 'reward',
        name: (
          <Flex fullWidth justifyContent="flex-end">
            <StyledHeaderTitle>
              {`${t('rewards.reward')} [MPRO]`}
              <InfoIcon
                iconProps={{ size: 18 }}
                tooltipProps={{
                  title: (
                    <>
                      {t('rewards.rewardsInfo')}
                      <StyledLink
                        href={import.meta.env.VITE_REWARD_SYSTEM_DOCS}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('rewards.rewardsInfoDoc')}
                      </StyledLink>
                    </>
                  ),
                  placement: 'top'
                }}
              />
            </StyledHeaderTitle>
          </Flex>
        ),
        gridColumns: 3,
        align: 'flex-end',
        customComponent(log: NodesByCycleIdData) {
          return (
            <Flex justifyContent="flex-end">
              <Text variant={TextVariant.BODY_SMALL_BOLD} isTrimmed linesToDisplay={1} color={colors.cyan}>
                {formatPrice(log.reward)}
              </Text>
            </Flex>
          )
        }
      }
    ],
    [logData.length]
  )

  return (
    <TableSquare
      plain
      loading={false}
      data={logData}
      emptyListComponent={<Text textAlign="center">{t('rewards.noActiveLogs')}</Text>}
      fullViewMediaquery={MediaQuery.tablet}
      fullViewCustomWidth={900}
      {...{ columns }}
    />
  )
})

export default NodesPerCycleLogView
